import { getBackendUrl } from '../config/api';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Calendar, 
  Download, 
  ChevronLeft, 
  ChevronRight,
  Clock,
  Users,
  GraduationCap,
  Building,
  BookOpen,
  Printer,
  CalendarDays,
  Grid3X3,
  Sparkles,
  RefreshCw
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { Checkbox } from './ui/checkbox';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import * as XLSX from 'xlsx';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

// Default time slots (will be replaced by dynamic slots including custom ones)
const DEFAULT_TIME_SLOTS = [
  '09:00-10:30',
  '10:30-12:00', 
  '12:00-13:30',
  '13:30-15:00',
  '15:00-16:30',
  '16:30-18:00'
];

// Helper function to format date in local timezone (avoids UTC conversion issues)
const formatDateLocal = (date) => {
  if (!date) return '';
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const EnhancedTimetable = () => {
  // Data state
  const [batches, setBatches] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [classrooms, setClassrooms] = useState([]);
  const [timeSlots, setTimeSlots] = useState(DEFAULT_TIME_SLOTS); // Dynamic time slots
  
  // Selection state - multi-select
  const [selectedBatches, setSelectedBatches] = useState([]);
  const [selectedTeachers, setSelectedTeachers] = useState([]);
  const [selectedClassrooms, setSelectedClassrooms] = useState([]);
  
  // Timetable data
  const [timetableData, setTimetableData] = useState(null);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // View mode
  const [viewMode, setViewMode] = useState('weekly');
  
  // Navigation
  const [currentWeekStart, setCurrentWeekStart] = useState(getCurrentWeekStart());
  const [currentMonth, setCurrentMonth] = useState(new Date());
  
  // Dropdown state
  const [openDropdown, setOpenDropdown] = useState(null); // 'batches', 'teachers', 'classrooms', or null
  const [searchTerm, setSearchTerm] = useState('');
  
  // Export
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportType, setExportType] = useState('');
  const [exportFromDate, setExportFromDate] = useState('');
  const [exportToDate, setExportToDate] = useState('');
  
  function getCurrentWeekStart() {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek;
    const monday = new Date(today);
    monday.setDate(today.getDate() + mondayOffset);
    return formatDateLocal(monday);
  }

  // Check if a date is a holiday
  const isHoliday = (dateString) => {
    return holidays.find(holiday => holiday.date === dateString);
  };

  // Fetch custom time slots from API
  const fetchTimeSlots = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/time-slots`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const customSlots = response.data || [];
      
      console.log('[TIMETABLE] Fetched custom slots from API:', customSlots);
      
      // Helper function to normalize time format to HH:MM
      const normalizeTime = (timeStr) => {
        if (!timeStr) return '';
        
        // Replace . with : (handle 10.30 -> 10:30)
        timeStr = timeStr.replace(/\./g, ':');
        
        // Split into parts
        const parts = timeStr.split(':');
        if (parts.length !== 2) return timeStr;
        
        let [hours, minutes] = parts;
        
        // Pad single digit hours (9 -> 09)
        hours = hours.padStart(2, '0');
        minutes = minutes.padStart(2, '0');
        
        return `${hours}:${minutes}`;
      };
      
      // Format custom slots to match time format (HH:MM-HH:MM)
      // Use the 'name' field if available, otherwise construct from start_time-end_time
      const customSlotStrings = customSlots
        .filter(slot => slot.is_active !== false) // Only active slots
        .map(slot => {
          let timeSlotStr;
          
          // Prefer the name field if it exists, otherwise construct from times
          if (slot.name) {
            timeSlotStr = slot.name;
          } else {
            timeSlotStr = `${slot.start_time}-${slot.end_time}`;
          }
          
          // Normalize the format to HH:MM-HH:MM
          if (timeSlotStr.includes('-')) {
            const [start, end] = timeSlotStr.split('-');
            return `${normalizeTime(start)}-${normalizeTime(end)}`;
          }
          
          return timeSlotStr;
        });
      
      console.log('[TIMETABLE] Formatted custom slot strings:', customSlotStrings);
      
      // Combine default and custom slots, removing duplicates
      const allSlots = [...new Set([...DEFAULT_TIME_SLOTS, ...customSlotStrings])];
      
      // Sort chronologically
      allSlots.sort((a, b) => {
        const [aStart] = a.split('-');
        const [bStart] = b.split('-');
        return aStart.localeCompare(bStart);
      });
      
      setTimeSlots(allSlots);
      console.log('[TIMETABLE] ✅ Loaded time slots:', allSlots.length, '(', customSlots.length, 'custom +', DEFAULT_TIME_SLOTS.length, 'default)');
      console.log('[TIMETABLE] All slots:', allSlots);
    } catch (error) {
      console.error('[TIMETABLE] ❌ Error fetching time slots:', error);
      setTimeSlots(DEFAULT_TIME_SLOTS); // Fallback to defaults
    }
  };

  // Get time slots to display - for batch filter, show only occupied slots
  const getDisplayTimeSlots = () => {
    // If batch filter is active (and no teacher/classroom filter), show only occupied slots
    if (selectedBatches.length > 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0) {
      const occupiedSlots = new Set();
      
      // Helper to normalize time slot format for comparison
      const normalizeTimeSlot = (timeSlot) => {
        if (!timeSlot || !timeSlot.includes('-')) return timeSlot;
        
        const [start, end] = timeSlot.split('-');
        const normalizeTime = (time) => {
          const parts = time.replace(/\./g, ':').split(':');
          if (parts.length === 2) {
            return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
          }
          return time;
        };
        
        return `${normalizeTime(start)}-${normalizeTime(end)}`;
      };
      
      // Collect all occupied time slots from timetable data (normalized)
      if (timetableData && timetableData.timetable_slots) {
        timetableData.timetable_slots.forEach(slot => {
          if (slot.time_slot) {
            const normalized = normalizeTimeSlot(slot.time_slot);
            occupiedSlots.add(normalized);
            console.log(`[getDisplayTimeSlots] Added slot: "${slot.time_slot}" → "${normalized}"`);
          }
        });
      }
      
      console.log('[getDisplayTimeSlots] Occupied slots:', Array.from(occupiedSlots));
      console.log('[getDisplayTimeSlots] All available time slots:', timeSlots);
      const filtered = timeSlots.filter(slot => occupiedSlots.has(slot));
      console.log('[getDisplayTimeSlots] Filtered slots to display:', filtered);
      
      // Return only time slots that have sessions (from dynamic timeSlots)
      return filtered;
    }
    
    // For teacher/classroom filters or no filter, show all slots
    return timeSlots;
  };

  function getCurrentMonthDates(monthDate) {
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    
    return {
      start: formatDateLocal(firstDay),
      end: formatDateLocal(lastDay),
      firstDay,
      lastDay
    };
  }

  function getCalendarDays(monthDate) {
    const { firstDay, lastDay } = getCurrentMonthDates(monthDate);
    const startDate = new Date(firstDay);
    
    // Start from the 1st of the month
    const firstDayOfMonth = new Date(startDate);
    firstDayOfMonth.setDate(1);
    
    // Get day of week for first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDayOfMonth.getDay();
    
    // If first day is not Monday, go back to previous Monday
    const daysToSubtract = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1; // 0 for Monday, 6 for Sunday
    const calendarStart = new Date(firstDayOfMonth);
    calendarStart.setDate(calendarStart.getDate() - daysToSubtract);
    
    // Generate 6 weeks of days (42 days total)
    const calendarDays = [];
    const currentDate = new Date(calendarStart);
    
    for (let i = 0; i < 42; i++) {
      calendarDays.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }
    
    return calendarDays;
  }

  // Load initial data
  useEffect(() => {
    fetchInitialData();
  }, []);

  // Fetch timetable when selections change
  useEffect(() => {
    // Always fetch timetable data for current week, even with no filters selected
    // This shows all sessions by default (user's request: "show current week by default")
    fetchTimetableData();
  }, [selectedBatches, selectedTeachers, selectedClassrooms, currentWeekStart, currentMonth, viewMode]);

  // Auto-refresh when user returns to page (to show deleted sessions removed)
  useEffect(() => {
    const handleVisibilityChange = () => {
      // When page becomes visible again, refetch timetable data
      if (!document.hidden) {
        console.log('[TIMETABLE] Page became visible - auto-refreshing data');
        fetchTimetableData();
      }
    };

    // Listen for visibility changes
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup listener on unmount
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [selectedBatches, selectedTeachers, selectedClassrooms, currentWeekStart, currentMonth, viewMode]);

  const fetchInitialData = async () => {
    setLoading(true);
    try {
      const currentYear = new Date().getFullYear();
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      // Exclude archived batches from Smart Time Tables
      const [batchesRes, teachersRes, classroomsRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/batches`, { headers, params: { include_archived: false } }),
        axios.get(`${API}/teachers`, { headers }),
        axios.get(`${API}/classrooms`, { headers }),
        axios.get(`${API}/holidays?year=${currentYear}`, { headers })
      ]);
      
      setBatches(batchesRes.data);
      setTeachers(teachersRes.data);
      setClassrooms(classroomsRes.data);
      setHolidays(holidaysRes.data);
      
      // Fetch custom time slots
      await fetchTimeSlots();
    } catch (error) {
      console.error('Error fetching initial data:', error);
      toast.error('Failed to load initial data');
    } finally {
      setLoading(false);
    }
  };

  const fetchTimetableData = async () => {
    setLoading(true);
    try {
      const allSlots = [];
      const seenSlots = new Set(); // To track unique slots and prevent duplicates
      
      // Determine date range
      let dateRange;
      if (viewMode === 'monthly') {
        dateRange = getCurrentMonthDates(currentMonth);
      } else {
        const weekEnd = new Date(currentWeekStart);
        weekEnd.setDate(weekEnd.getDate() + 5);
        dateRange = {
          start: currentWeekStart,
          end: formatDateLocal(weekEnd)
        };
      }

      // Helper function to create unique identifier for slots
      const teacherAlias = {};
      const getSlotKey = (slot) => {
        const date = slot.scheduled_date || slot.date;
        const day = slot.day;
        const timeSlot = slot.time_slot;
        const subject = slot.subject_name || '';
        const teacher = slot.teacher_name || '';
        const classroom = slot.classroom_name || '';
        return `${date}-${day}-${timeSlot}-${subject}-${teacher}-${classroom}`;
      };

      // Helper function to add unique slot
      const addUniqueSlot = (slot, source) => {
        const key = getSlotKey(slot);
        if (!seenSlots.has(key)) {
          seenSlots.add(key);
          allSlots.push({
            ...slot,
            source: source
          });
        }
      };

      // NEW: If no filters selected, fetch all sessions for current week/month
      if (selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0) {
        try {
          console.log('[DEFAULT VIEW] No filters selected - fetching all sessions for date range:', dateRange);
          const response = await axios.get(`${API}/sessions`, {
            params: {
              date_from: dateRange.start,
              date_to: dateRange.end
            }
          });
          
          if (response.data) {
            console.log('[DEFAULT VIEW] Fetched', response.data.length, 'total sessions');
            response.data.forEach(session => {
              addUniqueSlot(session, 'all');
            });
          }
        } catch (error) {
          console.error('[DEFAULT VIEW] Error fetching all sessions:', error);
        }
      }

      // Priority 1: Fetch batch timetables (most comprehensive data)
      if (selectedBatches.length > 0) {
        if (viewMode === 'weekly') {
          // PERFORMANCE FIX: Parallel API calls instead of sequential
          const batchPromises = selectedBatches.map(batchId => 
            axios.get(`${API}/timetable/batch/${batchId}`, {
              params: { week_start_date: currentWeekStart }
            }).catch(error => {
              console.error(`Error fetching batch ${batchId}:`, error);
              return null;
            })
          );
          
          const batchResponses = await Promise.all(batchPromises);
          
          batchResponses.forEach((response, index) => {
            if (response?.data?.timetable_slots) {
              const batchId = selectedBatches[index];
              response.data.timetable_slots.forEach(slot => {
                addUniqueSlot({
                  ...slot,
                  batch_id: batchId,
                  batch_name: response.data.batch?.name || 'Unknown Batch'
                }, 'batch');
              });
            }
          });
        } else {
          // Monthly view: Fetch all sessions for date range and filter by batch
          try {
            const response = await axios.get(`${API}/sessions`, {
              params: {
                date_from: dateRange.start,
                date_to: dateRange.end
              }
            });
            
            if (response.data) {
              // PERFORMANCE FIX: Create Set for faster lookup
              const selectedBatchSet = new Set(selectedBatches);
              
              response.data.forEach(session => {
                // Filter by selected batches (match by batch_id)
                const matchesBatch = selectedBatchSet.has(session.batch_id);
                
                if (matchesBatch) {
                  addUniqueSlot(session, 'batch');
                }
              });
            }
          } catch (error) {
            console.error('Error fetching sessions for monthly view:', error);
          }
        }
      }

      // Priority 2: Fetch teacher sessions (only if no batch data or additional teachers selected)
      if (selectedTeachers.length > 0) {
        // PERFORMANCE FIX: Parallel API calls for all teachers
        const teacherPromises = selectedTeachers.map(teacherId => {
          const params = { 
            teacher_id: teacherId,
            start_date: dateRange.start,
            end_date: dateRange.end
          };
          return axios.get(`${API}/sessions`, { params }).catch(error => {
            console.error(`Error fetching teacher ${teacherId}:`, error);
            return null;
          });
        });
        
        const teacherResponses = await Promise.all(teacherPromises);
        
        teacherResponses.forEach((response, index) => {
          if (response?.data) {
            response.data.forEach(session => {
              // Backend may match duplicate teacher records; map them to the selected id
              if (session.teacher_id) teacherAlias[session.teacher_id] = selectedTeachers[index];
              addUniqueSlot(session, 'teacher');
            });
          }
        });
      }

      // Priority 3: Fetch classroom sessions (only if not already covered by batch/teacher data)
      if (selectedClassrooms.length > 0) {
        // PERFORMANCE FIX: Parallel API calls for all classrooms
        const classroomPromises = selectedClassrooms.map(classroomId => {
          const params = { 
            classroom_id: classroomId,
            start_date: dateRange.start,
            end_date: dateRange.end
          };
          return axios.get(`${API}/sessions`, { params }).catch(error => {
            console.error(`Error fetching classroom ${classroomId}:`, error);
            return null;
          });
        });
        
        const classroomResponses = await Promise.all(classroomPromises);
        
        classroomResponses.forEach(response => {
          if (response?.data) {
            response.data.forEach(session => {
              // Add all sessions - API already filtered by date
              addUniqueSlot(session, 'classroom');
            });
          }
        });
      }

      setTimetableData({
        timetable_slots: allSlots.map(s =>
          teacherAlias[s.teacher_id] ? { ...s, teacher_id: teacherAlias[s.teacher_id] } : s
        )
      });
    } catch (error) {
      console.error('Error fetching timetable:', error);
      toast.error('Failed to load timetable data');
    } finally {
      setLoading(false);
    }
  };

  // Multi-select handlers - Allow mixing different types
  const handleBatchSelection = (batchId, checked) => {
    if (checked) {
      setSelectedBatches(prev => [...prev, batchId]);
    } else {
      setSelectedBatches(prev => prev.filter(id => id !== batchId));
    }
  };

  const handleTeacherSelection = (teacherId, checked) => {
    if (checked) {
      setSelectedTeachers(prev => [...prev, teacherId]);
    } else {
      setSelectedTeachers(prev => prev.filter(id => id !== teacherId));
    }
  };

  const handleClassroomSelection = (classroomId, checked) => {
    if (checked) {
      setSelectedClassrooms(prev => [...prev, classroomId]);
    } else {
      setSelectedClassrooms(prev => prev.filter(id => id !== classroomId));
    }
  };

  const selectAllBatches = () => {
    if (selectedBatches.length === batches.length) {
      setSelectedBatches([]);
    } else {
      setSelectedBatches(batches.map(b => b.id));
    }
  };

  const selectAllTeachers = () => {
    if (selectedTeachers.length === teachers.length) {
      setSelectedTeachers([]);
    } else {
      setSelectedTeachers(teachers.map(t => t.id));
    }
  };

  const selectAllClassrooms = () => {
    if (selectedClassrooms.length === classrooms.length) {
      setSelectedClassrooms([]);
    } else {
      setSelectedClassrooms(classrooms.map(c => c.id));
    }
  };

  // Dropdown helpers
  const toggleDropdown = (dropdownName) => {
    setOpenDropdown(openDropdown === dropdownName ? null : dropdownName);
    setSearchTerm('');
  };

  const closeDropdown = () => {
    setOpenDropdown(null);
    setSearchTerm('');
  };

  const getFilteredItems = (items, type) => {
    if (!searchTerm) return items;
    return items.filter(item => {
      const searchStr = type === 'batches' 
        ? `${item.name} ${item.stream || ''}`.toLowerCase()
        : item.name.toLowerCase();
      return searchStr.includes(searchTerm.toLowerCase());
    });
  };

  // Navigation
  const navigateWeek = (direction) => {
    const currentDate = new Date(currentWeekStart);
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentWeekStart(newDate.toISOString().split('T')[0]);
  };

  const navigateMonth = (direction) => {
    const newMonth = new Date(currentMonth);
    newMonth.setMonth(currentMonth.getMonth() + direction);
    setCurrentMonth(newMonth);
  };

  // Helper functions
  const getSlotForDayTime = (day, timeSlot, date = null) => {
    if (!timetableData?.timetable_slots) return [];
    
    // Helper to normalize time slot format for comparison
    const normalizeTimeSlot = (ts) => {
      if (!ts || !ts.includes('-')) return ts;
      const [start, end] = ts.split('-');
      const normalizeTime = (time) => {
        const parts = time.replace(/\./g, ':').split(':');
        if (parts.length === 2) {
          return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
        }
        return time;
      };
      return `${normalizeTime(start)}-${normalizeTime(end)}`;
    };
    
    // Normalize the input time slot for comparison
    const normalizedTimeSlot = normalizeTimeSlot(timeSlot);
    
    return timetableData.timetable_slots.filter(slot => {
      const matchesDay = slot.day === day;
      // Normalize slot.time_slot before comparing
      const matchesTime = normalizeTimeSlot(slot.time_slot) === normalizedTimeSlot;
      
      // Filter by date if provided
      let matchesDate = true;
      if (date) {
        const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
        matchesDate = slot.scheduled_date === dateStr;
      }
      
      // Filter by selected teachers (if any)
      let matchesTeacher = true;
      if (selectedTeachers.length > 0) {
        matchesTeacher = selectedTeachers.includes(slot.teacher_id);
      }
      
      // Filter by selected classrooms (if any)
      let matchesClassroom = true;
      if (selectedClassrooms.length > 0) {
        matchesClassroom = selectedClassrooms.includes(slot.classroom_id);
      }
      
      // Filter by selected batches (if any)
      let matchesBatch = true;
      if (selectedBatches.length > 0) {
        matchesBatch = selectedBatches.includes(slot.batch_id);
      }
      
      return matchesDay && matchesTime && matchesDate && matchesTeacher && matchesClassroom && matchesBatch;
    });
  };

  const getSlotsForDate = (date) => {
    if (!timetableData?.timetable_slots) {
      return [];
    }
    const dateStr = typeof date === 'string' ? date : date.toISOString().split('T')[0];
    
    const filtered = timetableData.timetable_slots.filter(slot => {
      const matchesDate = slot.scheduled_date === dateStr;
      
      // Filter by selected teachers (if any)
      let matchesTeacher = true;
      if (selectedTeachers.length > 0) {
        matchesTeacher = selectedTeachers.includes(slot.teacher_id);
      }
      
      // Filter by selected classrooms (if any)
      let matchesClassroom = true;
      if (selectedClassrooms.length > 0) {
        matchesClassroom = selectedClassrooms.includes(slot.classroom_id);
      }
      
      // Filter by selected batches (if any)
      let matchesBatch = true;
      if (selectedBatches.length > 0) {
        matchesBatch = selectedBatches.includes(slot.batch_id);
      }
      
      return matchesDate && matchesTeacher && matchesClassroom && matchesBatch;
    });
    
    if (filtered.length > 0) {
      console.log(`[GET SLOTS DEBUG] Found ${filtered.length} slots for date ${dateStr} after filtering`);
    }
    
    return filtered;
  };

  // Export functions
  const exportTimetable = (format) => {
    if (selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0) {
      toast.error('Please select at least one filter first');
      return;
    }
    
    if (format === 'print') {
      window.print();
      return;
    }

    setExportType(format);
    
    if (viewMode === 'monthly') {
      const monthDates = getCurrentMonthDates(currentMonth);
      setExportFromDate(monthDates.start);
      setExportToDate(monthDates.end);
    } else {
      setExportFromDate(currentWeekStart);
      const endDate = new Date(currentWeekStart);
      endDate.setDate(endDate.getDate() + 5);
      setExportToDate(endDate.toISOString().split('T')[0]);
    }
    
    setShowExportModal(true);
  };

  const handleExportConfirm = async () => {
    if (!exportFromDate || !exportToDate) {
      toast.error('Please select both dates');
      return;
    }

    if (new Date(exportFromDate) > new Date(exportToDate)) {
      toast.error('From date must be before or equal to To date');
      return;
    }

    setShowExportModal(false);

    try {
      if (exportType === 'pdf') {
        await exportToPDF();
      } else if (exportType === 'excel') {
        await exportToExcel();
      }
    } catch (error) {
      console.error('Export failed:', error);
      toast.error(`Failed to export ${exportType.toUpperCase()}`);
    }
  };

  const exportToPDF = async () => {
    toast.info('Generating PDF...');
    
    try {
      const pdf = new jsPDF('l', 'mm', 'a4'); // Landscape for better table view
      let yPosition = 20;
      
      // Add NIF Global Logo with improved quality
      try {
        const logoResponse = await fetch('/nif_logo_large.png');
        if (logoResponse.ok) {
          const logoBlob = await logoResponse.blob();
          const logoDataUrl = await new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.readAsDataURL(logoBlob);
          });
          pdf.addImage(logoDataUrl, 'PNG', 240, 8, 40, 35, '', 'FAST');
        }
      } catch (logoError) {
        console.warn('Could not load logo:', logoError);
      }
      
      // Title and branding
      pdf.setFontSize(18);
      pdf.text('Smart Manager - Smart Time Tables', 15, yPosition);
      yPosition += 8;
      
      pdf.setFontSize(12);
      pdf.text('NIF Global Pimpri Chinchwad', 15, yPosition);
      yPosition += 10;
      
      // Export details
      pdf.setFontSize(10);
      pdf.text(`Date Range: ${exportFromDate} to ${exportToDate}`, 15, yPosition);
      yPosition += 6;
      pdf.text(`View Mode: ${viewMode.toUpperCase()}`, 15, yPosition);
      yPosition += 6;
      
      // Filter information
      if (selectedBatches.length > 0) {
        const names = selectedBatches.map(id => batches.find(b => b.id === id)?.name).join(', ');
        pdf.text(`Batches: ${names}`, 15, yPosition);
        yPosition += 5;
      }
      if (selectedTeachers.length > 0) {
        const names = selectedTeachers.map(id => teachers.find(t => t.id === id)?.name).join(', ');
        pdf.text(`Teachers: ${names}`, 15, yPosition);
        yPosition += 5;
      }
      if (selectedClassrooms.length > 0) {
        const names = selectedClassrooms.map(id => classrooms.find(c => c.id === id)?.name).join(', ');
        pdf.text(`Classrooms: ${names}`, 15, yPosition);
        yPosition += 5;
      }
      
      yPosition += 10;
      
      // Group sessions by date
      const slotsByDate = {};
      if (timetableData?.timetable_slots) {
        timetableData.timetable_slots.forEach(slot => {
          const date = slot.scheduled_date;
          if (!slotsByDate[date]) slotsByDate[date] = [];
          slotsByDate[date].push(slot);
        });
      }
      
      // Get sorted dates
      const dates = Object.keys(slotsByDate).sort();
      
      if (dates.length === 0) {
        pdf.setFontSize(12);
        pdf.text('No sessions scheduled for the selected date range', 15, yPosition);
      } else {
        // Prepare table data - calendar style with dates as columns
        const tableHeaders = ['Time Slot'];
        dates.forEach(date => {
          const dateObj = new Date(date + 'T00:00:00');
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = dateObj.getDate();
          const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
          tableHeaders.push(`${dayName}\n${dayNum} ${month}`);
        });
        
        // Get all unique time slots
        const allTimeSlots = new Set();
        dates.forEach(date => {
          slotsByDate[date].forEach(slot => {
            allTimeSlots.add(slot.time_slot);
          });
        });
        
        const sortedTimeSlots = Array.from(allTimeSlots).sort();
        
        // Create table data
        const tableData = [];
        sortedTimeSlots.forEach(timeSlot => {
          const row = [timeSlot];
          
          dates.forEach(date => {
            const sessionsAtTime = slotsByDate[date].filter(s => s.time_slot === timeSlot);
            
            if (sessionsAtTime.length === 0) {
              row.push('');
            } else if (sessionsAtTime.length === 1) {
              const slot = sessionsAtTime[0];
              row.push(`${slot.subject_name}\n${slot.teacher_name}\n${slot.classroom_name}\n${slot.batch_name}`);
            } else {
              // Multiple sessions at same time
              const combined = sessionsAtTime.map(s => 
                `${s.subject_name}\n(${s.teacher_name},\n${s.classroom_name})`
              ).join('\n---\n');
              row.push(combined);
            }
          });
          
          tableData.push(row);
        });
        
        // Calculate column widths dynamically
        const pageWidth = 297; // A4 landscape width
        const leftMargin = 15;
        const rightMargin = 15;
        const availableWidth = pageWidth - leftMargin - rightMargin;
        const timeColumnWidth = 25;
        const dateColumnWidth = (availableWidth - timeColumnWidth) / dates.length;
        
        const columnStyles = {};
        columnStyles[0] = { cellWidth: timeColumnWidth };
        for (let i = 1; i <= dates.length; i++) {
          columnStyles[i] = { cellWidth: dateColumnWidth };
        }
        
        // Generate table
        pdf.autoTable({
          head: [tableHeaders],
          body: tableData,
          startY: yPosition,
          theme: 'grid',
          styles: {
            fontSize: 8,
            cellPadding: 3,
            overflow: 'linebreak',
            valign: 'top'
          },
          headStyles: {
            fillColor: [41, 128, 185],
            textColor: 255,
            fontStyle: 'bold',
            halign: 'center'
          },
          columnStyles: columnStyles,
          didDrawPage: function(data) {
            // Add page numbers
            const pageCount = pdf.internal.getNumberOfPages();
            pdf.setFontSize(10);
            pdf.text(
              `Page ${pdf.internal.getCurrentPageInfo().pageNumber} of ${pageCount}`,
              pdf.internal.pageSize.getWidth() / 2,
              pdf.internal.pageSize.getHeight() - 10,
              { align: 'center' }
            );
          }
        });
      }
      
      // Save PDF
      const fileName = getExportTitle() + '.pdf';
      const pdfBlob = pdf.output('blob');
      
      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [{ description: 'PDF files', accept: { 'application/pdf': ['.pdf'] } }]
          });
          const writable = await fileHandle.createWritable();
          await writable.write(pdfBlob);
          await writable.close();
          toast.success('PDF saved successfully!');
          return;
        } catch (fsError) {
          if (fsError.name !== 'AbortError') {
            console.warn('File System Access API failed:', fsError);
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(pdfBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export failed:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const exportToExcel = async () => {
    toast.info('Generating Excel...');
    
    try {
      const excelData = [];
      
      // Header
      excelData.push(['Smart Manager - Smart Time Tables']);
      excelData.push(['NIF Global Pimpri Chinchwad']);
      excelData.push([]);
      excelData.push([`Date Range: ${exportFromDate} to ${exportToDate}`]);
      excelData.push([`View Mode: ${viewMode.toUpperCase()}`]);
      
      // Filter info
      if (selectedBatches.length > 0) {
        const names = selectedBatches.map(id => batches.find(b => b.id === id)?.name).join(', ');
        excelData.push([`Batches: ${names}`]);
      }
      if (selectedTeachers.length > 0) {
        const names = selectedTeachers.map(id => teachers.find(t => t.id === id)?.name).join(', ');
        excelData.push([`Teachers: ${names}`]);
      }
      if (selectedClassrooms.length > 0) {
        const names = selectedClassrooms.map(id => classrooms.find(c => c.id === id)?.name).join(', ');
        excelData.push([`Classrooms: ${names}`]);
      }
      
      excelData.push([]);
      excelData.push([]);
      
      // Group sessions by date
      const slotsByDate = {};
      if (timetableData?.timetable_slots) {
        timetableData.timetable_slots.forEach(slot => {
          const date = slot.scheduled_date;
          if (!slotsByDate[date]) slotsByDate[date] = [];
          slotsByDate[date].push(slot);
        });
      }
      
      // Get sorted dates
      const dates = Object.keys(slotsByDate).sort();
      
      if (dates.length === 0) {
        excelData.push(['No sessions scheduled for the selected date range']);
      } else {
        // Create calendar-style layout
        // Header row with dates
        const dateHeaders = ['Time Slot'];
        dates.forEach(date => {
          const dateObj = new Date(date + 'T00:00:00');
          const dayName = dateObj.toLocaleDateString('en-US', { weekday: 'short' });
          const dayNum = dateObj.getDate();
          const month = dateObj.toLocaleDateString('en-US', { month: 'short' });
          dateHeaders.push(`${dayName} ${dayNum} ${month}`);
        });
        excelData.push(dateHeaders);
        
        // Get all unique time slots
        const allTimeSlots = new Set();
        dates.forEach(date => {
          slotsByDate[date].forEach(slot => {
            allTimeSlots.add(slot.time_slot);
          });
        });
        
        const sortedTimeSlots = Array.from(allTimeSlots).sort();
        
        // Create rows for each time slot
        sortedTimeSlots.forEach(timeSlot => {
          const row = [timeSlot];
          
          dates.forEach(date => {
            const sessionsAtTime = slotsByDate[date].filter(s => s.time_slot === timeSlot);
            
            if (sessionsAtTime.length === 0) {
              row.push('');
            } else if (sessionsAtTime.length === 1) {
              const slot = sessionsAtTime[0];
              row.push(`${slot.subject_name}\n${slot.teacher_name}\n${slot.classroom_name}\n${slot.batch_name}`);
            } else {
              // Multiple sessions at same time
              const combined = sessionsAtTime.map(s => 
                `${s.subject_name} (${s.teacher_name}, ${s.classroom_name})`
              ).join('\n');
              row.push(combined);
            }
          });
          
          excelData.push(row);
        });
      }
      
      // Create workbook
      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(excelData);
      
      // Set column widths - first column for time, others for dates
      const colWidths = [{ wch: 15 }]; // Time slot column
      for (let i = 0; i < dates.length; i++) {
        colWidths.push({ wch: 30 }); // Date columns
      }
      ws['!cols'] = colWidths;
      
      // Set row heights for better readability
      ws['!rows'] = excelData.map((row, idx) => {
        if (idx > 8 && row.length > 1) { // Data rows
          return { hpt: 60 }; // Height in points
        }
        return {};
      });
      
      XLSX.utils.book_append_sheet(wb, ws, 'Timetable');
      
      // Save Excel
      const fileName = getExportTitle() + '.xlsx';
      const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
      const excelBlob = new Blob([excelBuffer], { 
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
      });
      
      if (window.showSaveFilePicker) {
        try {
          const fileHandle = await window.showSaveFilePicker({
            suggestedName: fileName,
            types: [{ 
              description: 'Excel files', 
              accept: { 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'] } 
            }]
          });
          const writable = await fileHandle.createWritable();
          await writable.write(excelBlob);
          await writable.close();
          toast.success('Excel saved successfully!');
          return;
        } catch (fsError) {
          if (fsError.name !== 'AbortError') {
            console.warn('File System Access API failed:', fsError);
          }
        }
      }
      
      // Fallback download
      const url = URL.createObjectURL(excelBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 100);
      
      toast.success('Excel exported successfully!');
    } catch (error) {
      console.error('Excel export failed:', error);
      toast.error('Failed to generate Excel file');
    }
  };

  const getExportTitle = () => {
    const dateRange = `${exportFromDate}_to_${exportToDate}`;
    
    if (selectedBatches.length > 0) {
      return selectedBatches.length === 1 
        ? `Batch_${batches.find(b => b.id === selectedBatches[0])?.name}_${dateRange}`
        : `Batches_${selectedBatches.length}_${dateRange}`;
    } else if (selectedTeachers.length > 0) {
      return selectedTeachers.length === 1
        ? `Teacher_${teachers.find(t => t.id === selectedTeachers[0])?.name}_${dateRange}`
        : `Teachers_${selectedTeachers.length}_${dateRange}`;
    } else if (selectedClassrooms.length > 0) {
      return selectedClassrooms.length === 1
        ? `Classroom_${classrooms.find(c => c.id === selectedClassrooms[0])?.name}_${dateRange}`
        : `Classrooms_${selectedClassrooms.length}_${dateRange}`;
    }
    return `Smart_Timetable_${dateRange}`;
  };

  if (loading && selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Smart Time Tables</h1>
          <p className="text-gray-600">Advanced timetable views with monthly/weekly modes and multi-select filtering</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <Button 
            variant="outline" 
            onClick={() => {
              console.log('[TIMETABLE] Manual refresh triggered');
              fetchTimetableData();
              toast.success('Timetable refreshed');
            }}
            className="text-blue-600 border-blue-200 hover:bg-blue-50"
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
          <Button 
            variant="outline" 
            onClick={() => exportTimetable('pdf')}
            disabled={selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
          <Button 
            variant="outline" 
            onClick={() => exportTimetable('excel')}
            disabled={selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0}
          >
            <Download className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button 
            variant="outline" 
            onClick={() => exportTimetable('print')}
            disabled={selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0}
          >
            <Printer className="w-4 h-4 mr-2" />
            Print
          </Button>
        </div>
      </div>

      {/* View Mode Toggle */}
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">View Mode</h3>
            <Tabs value={viewMode} onValueChange={setViewMode} className="w-auto">
              <TabsList>
                <TabsTrigger value="weekly" className="flex items-center">
                  <Calendar className="w-4 h-4 mr-2" />
                  Weekly
                </TabsTrigger>
                <TabsTrigger value="monthly" className="flex items-center">
                  <CalendarDays className="w-4 h-4 mr-2" />
                  Monthly
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Unified Multi-Select Filters */}
      <Card>
        <CardContent className="p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Filters - Select Multiple Entities</h3>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* Batches Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Users className="w-4 h-4 mr-2" />
                Batches
              </label>
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('batches')}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span className="text-sm text-gray-700">
                    {selectedBatches.length === 0
                      ? 'Select Batches...'
                      : selectedBatches.length === batches.length
                      ? `All Batches (${batches.length})`
                      : `${selectedBatches.length} Selected`}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${openDropdown === 'batches' ? 'rotate-90' : ''}`} />
                </button>
                
                {openDropdown === 'batches' && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={closeDropdown}></div>
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 space-y-2">
                        <input
                          type="text"
                          placeholder="Search batches..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); selectAllBatches(); }}
                            className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            {selectedBatches.length === batches.length ? 'Clear All' : 'Select All'}
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-60 p-2">
                        {getFilteredItems(batches, 'batches').map(batch => (
                          <div
                            key={batch.id}
                            onClick={(e) => { e.stopPropagation(); handleBatchSelection(batch.id, !selectedBatches.includes(batch.id)); }}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedBatches.includes(batch.id)}
                              onCheckedChange={(checked) => handleBatchSelection(batch.id, checked)}
                            />
                            <label className="text-sm cursor-pointer flex-1">
                              {batch.name}
                              {batch.stream && <span className="text-gray-500"> ({batch.stream})</span>}
                            </label>
                          </div>
                        ))}
                        {getFilteredItems(batches, 'batches').length === 0 && (
                          <div className="text-center text-sm text-gray-500 py-4">No batches found</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Teachers Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <GraduationCap className="w-4 h-4 mr-2" />
                Teachers
              </label>
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('teachers')}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span className="text-sm text-gray-700">
                    {selectedTeachers.length === 0
                      ? 'Select Teachers...'
                      : selectedTeachers.length === teachers.length
                      ? `All Teachers (${teachers.length})`
                      : `${selectedTeachers.length} Selected`}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${openDropdown === 'teachers' ? 'rotate-90' : ''}`} />
                </button>
                
                {openDropdown === 'teachers' && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={closeDropdown}></div>
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 space-y-2">
                        <input
                          type="text"
                          placeholder="Search teachers..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); selectAllTeachers(); }}
                            className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            {selectedTeachers.length === teachers.length ? 'Clear All' : 'Select All'}
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-60 p-2">
                        {getFilteredItems(teachers, 'teachers').map(teacher => (
                          <div
                            key={teacher.id}
                            onClick={(e) => { e.stopPropagation(); handleTeacherSelection(teacher.id, !selectedTeachers.includes(teacher.id)); }}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedTeachers.includes(teacher.id)}
                              onCheckedChange={(checked) => handleTeacherSelection(teacher.id, checked)}
                            />
                            <label className="text-sm cursor-pointer flex-1">
                              {teacher.name}
                            </label>
                          </div>
                        ))}
                        {getFilteredItems(teachers, 'teachers').length === 0 && (
                          <div className="text-center text-sm text-gray-500 py-4">No teachers found</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Classrooms Dropdown */}
            <div className="relative">
              <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center">
                <Building className="w-4 h-4 mr-2" />
                Classrooms
              </label>
              <div className="relative">
                <button
                  onClick={() => toggleDropdown('classrooms')}
                  className="w-full flex items-center justify-between px-4 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <span className="text-sm text-gray-700">
                    {selectedClassrooms.length === 0
                      ? 'Select Classrooms...'
                      : selectedClassrooms.length === classrooms.length
                      ? `All Classrooms (${classrooms.length})`
                      : `${selectedClassrooms.length} Selected`}
                  </span>
                  <ChevronRight className={`w-4 h-4 transition-transform ${openDropdown === 'classrooms' ? 'rotate-90' : ''}`} />
                </button>
                
                {openDropdown === 'classrooms' && (
                  <>
                    <div className="fixed inset-0 z-10" onClick={closeDropdown}></div>
                    <div className="absolute z-20 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg max-h-80 overflow-hidden">
                      <div className="p-2 border-b border-gray-200 space-y-2">
                        <input
                          type="text"
                          placeholder="Search classrooms..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          onClick={(e) => e.stopPropagation()}
                        />
                        <div className="flex gap-2">
                          <button
                            onClick={(e) => { e.stopPropagation(); selectAllClassrooms(); }}
                            className="flex-1 px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700"
                          >
                            {selectedClassrooms.length === classrooms.length ? 'Clear All' : 'Select All'}
                          </button>
                        </div>
                      </div>
                      <div className="overflow-y-auto max-h-60 p-2">
                        {getFilteredItems(classrooms, 'classrooms').map(classroom => (
                          <div
                            key={classroom.id}
                            onClick={(e) => { e.stopPropagation(); handleClassroomSelection(classroom.id, !selectedClassrooms.includes(classroom.id)); }}
                            className="flex items-center space-x-2 p-2 hover:bg-gray-100 rounded cursor-pointer"
                          >
                            <Checkbox
                              checked={selectedClassrooms.includes(classroom.id)}
                              onCheckedChange={(checked) => handleClassroomSelection(classroom.id, checked)}
                            />
                            <label className="text-sm cursor-pointer flex-1">
                              {classroom.name}
                            </label>
                          </div>
                        ))}
                        {getFilteredItems(classrooms, 'classrooms').length === 0 && (
                          <div className="text-center text-sm text-gray-500 py-4">No classrooms found</div>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Current selections */}
          {(selectedBatches.length > 0 || selectedTeachers.length > 0 || selectedClassrooms.length > 0) && (
            <div className="mt-4 p-3 bg-blue-50 rounded-lg">
              <p className="text-sm font-medium text-blue-800 mb-1">Active Filters:</p>
              <div className="text-sm text-blue-700">
                {selectedBatches.length > 0 && (
                  <span className="mr-4">Batches: {selectedBatches.length}</span>
                )}
                {selectedTeachers.length > 0 && (
                  <span className="mr-4">Teachers: {selectedTeachers.length}</span>
                )}
                {selectedClassrooms.length > 0 && (
                  <span>Classrooms: {selectedClassrooms.length}</span>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Navigation */}
      {(selectedBatches.length > 0 || selectedTeachers.length > 0 || selectedClassrooms.length > 0) && (
        <>
          {/* Prominent Filter Display */}
          <Card className="bg-gradient-to-r from-blue-500 to-indigo-600 border-0">
            <CardContent className="p-6">
              <div className="flex items-center justify-center flex-wrap gap-4 text-white">
                {selectedBatches.length > 0 && (
                  <div className="text-center">
                    <div className="text-sm font-medium opacity-90 mb-1">
                      {selectedBatches.length === 1 ? 'Batch' : 'Batches'}
                    </div>
                    <div className="text-lg font-bold">
                      {selectedBatches.map(id => {
                        const batch = batches.find(b => b.id === id);
                        return batch?.name || 'Unknown';
                      }).join(' | ')}
                    </div>
                  </div>
                )}
                
                {selectedTeachers.length > 0 && (
                  <div className="text-center">
                    {selectedBatches.length > 0 && <div className="text-2xl mx-4">•</div>}
                    <div className="text-sm font-medium opacity-90 mb-1">
                      {selectedTeachers.length === 1 ? 'Teacher' : 'Teachers'}
                    </div>
                    <div className="text-lg font-bold">
                      {selectedTeachers.map(id => {
                        const teacher = teachers.find(t => t.id === id);
                        return teacher?.name || 'Unknown';
                      }).join(' | ')}
                    </div>
                  </div>
                )}
                
                {selectedClassrooms.length > 0 && (
                  <div className="text-center">
                    {(selectedBatches.length > 0 || selectedTeachers.length > 0) && <div className="text-2xl mx-4">•</div>}
                    <div className="text-sm font-medium opacity-90 mb-1">
                      {selectedClassrooms.length === 1 ? 'Classroom' : 'Classrooms'}
                    </div>
                    <div className="text-lg font-bold">
                      {selectedClassrooms.map(id => {
                        const classroom = classrooms.find(c => c.id === id);
                        return classroom?.name || 'Unknown';
                      }).join(' | ')}
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-center space-x-4">
                <Button
                  variant="outline"
                  onClick={() => viewMode === 'weekly' ? navigateWeek(-1) : navigateMonth(-1)}
                >
                  <ChevronLeft className="w-4 h-4 mr-2" />
                  Previous {viewMode === 'weekly' ? 'Week' : 'Month'}
                </Button>
                
                <div className="text-center">
                  {viewMode === 'weekly' ? (
                    <>
                      <div className="font-semibold">
                        {new Date(currentWeekStart).toLocaleDateString()}
                      </div>
                      <div className="text-sm text-gray-500">Week Starting</div>
                    </>
                  ) : (
                    <>
                      <div className="font-semibold">
                        {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                      <div className="text-sm text-gray-500">Monthly View</div>
                    </>
                  )}
                </div>
                
                <Button
                  variant="outline"
                  onClick={() => viewMode === 'weekly' ? navigateWeek(1) : navigateMonth(1)}
                >
                  Next {viewMode === 'weekly' ? 'Week' : 'Month'}
                  <ChevronRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {/* Timetable Display */}
      {(selectedBatches.length > 0 || selectedTeachers.length > 0 || selectedClassrooms.length > 0) && (
        <Tabs value={viewMode} onValueChange={setViewMode}>
          <TabsContent value="weekly">
            {/* Weekly View */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Calendar className="w-5 h-5 mr-2" />
                  Weekly View
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <div className="min-w-[900px]">
                      {/* Header */}
                      <div className="grid grid-cols-8 bg-gray-50 border-b">
                        <div className="p-3 font-medium border-r">
                          <Clock className="w-4 h-4 inline mr-2" />
                          Time
                        </div>
                        {DAYS.map((day, index) => {
                          const weekDates = [];
                          const startDate = new Date(currentWeekStart);
                          for (let i = 0; i < 7; i++) {
                            const date = new Date(startDate);
                            date.setDate(startDate.getDate() + i);
                            weekDates.push(date);
                          }
                          const dayDate = weekDates[index];
                          
                          const dayDateString = formatDateLocal(dayDate);
                          const holidayInfo = isHoliday(dayDateString);
                          
                          return (
                            <div key={day} className={`p-3 text-center border-r last:border-r-0 ${holidayInfo ? 'bg-orange-50' : ''}`}>
                              <div className="font-medium">{day}</div>
                              <div className="text-xs text-gray-500">
                                {dayDate?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                              </div>
                              {holidayInfo && (
                                <div className="flex items-center justify-center mt-1">
                                  <Sparkles className="w-3 h-3 text-orange-600 mr-1" />
                                  <span className="text-xs text-orange-700 font-medium">Holiday</span>
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>

                      {/* Time slots */}
                      {getDisplayTimeSlots().map(timeSlot => (
                        <div key={timeSlot} className="grid grid-cols-8 border-b">
                          <div className="p-3 bg-gray-50 border-r font-medium">
                            {timeSlot}
                          </div>
                          
                          {DAYS.map((day, dayIndex) => {
                            const slots = getSlotForDayTime(day, timeSlot);
                            
                            // Get date for this day
                            const weekDates = [];
                            for (let i = 0; i < 7; i++) {
                              const date = new Date(currentWeekStart);
                              date.setDate(date.getDate() + i);
                              weekDates.push(date);
                            }
                            const dayDate = weekDates[dayIndex];
                            const dayDateString = formatDateLocal(dayDate);
                            const holidayInfo = isHoliday(dayDateString);
                            
                            return (
                              <div key={`${day}-${timeSlot}`} className={`p-2 border-r last:border-r-0 min-h-[80px] ${holidayInfo ? 'bg-orange-25' : ''}`}>
                                {holidayInfo && slots.length === 0 && (
                                  <div className={`flex flex-col items-center justify-center h-full ${holidayInfo.is_staff_holiday ? 'text-red-600' : 'text-orange-600'}`}>
                                    <Sparkles className="w-4 h-4 mb-1" />
                                    <div className="text-xs font-medium">{holidayInfo.name}</div>
                                    <div className="text-xs capitalize text-orange-500">{holidayInfo.holiday_type}</div>
                                    {holidayInfo.is_staff_holiday && (
                                      <div className="text-xs font-bold text-red-600 mt-1">Staff Off</div>
                                    )}
                                  </div>
                                )}
                                {slots.length > 0 ? (
                                  <div className="space-y-1">
                                    {holidayInfo && (
                                      <div className={`p-1 rounded text-xs mb-2 border ${holidayInfo.is_staff_holiday ? 'bg-red-100 border-red-200' : 'bg-orange-100 border-orange-200'}`}>
                                        <div className={`flex items-center justify-center ${holidayInfo.is_staff_holiday ? 'text-red-700' : 'text-orange-700'}`}>
                                          <Sparkles className="w-3 h-3 mr-1" />
                                          <span className="font-medium">
                                            {holidayInfo.is_staff_holiday ? 'Staff Holiday' : 'Holiday'}: {holidayInfo.name}
                                          </span>
                                        </div>
                                      </div>
                                    )}
                                    {slots.map((slot, idx) => (
                                      <div key={idx} className="bg-blue-50 p-2 rounded text-xs">
                                        <div className="font-medium text-blue-900 flex items-center">
                                          <BookOpen className="w-3 h-3 mr-1" />
                                          {slot.subject_name}
                                        </div>
                                        <div className="text-gray-600 flex items-center mt-1">
                                          <GraduationCap className="w-3 h-3 mr-1" />
                                          {slot.teacher_name}
                                        </div>
                                        <div className="text-gray-600 flex items-center">
                                          <Building className="w-3 h-3 mr-1" />
                                          {slot.classroom_name}
                                        </div>
                                        {slot.batch_name && (
                                          <Badge variant="outline" className="text-xs mt-1">
                                            {slot.batch_name}
                                          </Badge>
                                        )}
                                      </div>
                                    ))}
                                  </div>
                                ) : !holidayInfo && (
                                  <div className="flex items-center justify-center h-full text-gray-300">
                                    <span className="text-xs">Free</span>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Lunch Break Note - Below the Weekly View Card */}
            {!loading && timetableData?.timetable_slots?.length > 0 && (
              <div className="mx-0 px-4 py-3 bg-amber-100 border-2 border-amber-300 rounded-lg mt-2 shadow-sm">
                <p className="text-sm text-amber-900 font-semibold text-center">
                  📢 Note: 1:15 PM to 1:45 PM will be Lunch Break for all staff and students
                </p>
              </div>
            )}
          </TabsContent>

          <TabsContent value="monthly">
            {/* Monthly View */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <div className="flex items-center">
                    <CalendarDays className="w-5 h-5 mr-2" />
                    Monthly View - {currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                  </div>
                  <div className="text-sm text-gray-500">
                    Detailed Schedule Calendar
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <div className="flex justify-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : !timetableData || !timetableData.timetable_slots ? (
                  <div className="flex justify-center py-8">
                    <div className="text-gray-500">Select a filter to view timetable</div>
                  </div>
                ) : (
                  <div className="overflow-auto max-h-[800px]">
                    <div className="min-w-[900px]" key={`monthly-${timetableData.timetable_slots.length}-${currentMonth.getTime()}`}>
                      {/* Calendar header */}
                      <div className="grid grid-cols-7 gap-1 mb-2">
                        {DAYS.map(day => (
                          <div key={day} className="p-2 text-center font-medium bg-gray-100 rounded">
                            {day.slice(0, 3)}
                          </div>
                        ))}
                      </div>

                      {/* Calendar grid */}
                      <div className="grid grid-cols-7 gap-2">
                        {(() => {
                          const calendarDays = getCalendarDays(currentMonth);
                          console.log('[CALENDAR DEBUG] Total calendar days generated:', calendarDays.length);
                          console.log('[CALENDAR DEBUG] Date range:', 
                            calendarDays[0]?.toISOString().split('T')[0], 
                            'to', 
                            calendarDays[calendarDays.length - 1]?.toISOString().split('T')[0]
                          );
                          return calendarDays.map((date, index) => {
                            const isCurrentMonth = date.getMonth() === currentMonth.getMonth();
                            const dateStr = formatDateLocal(date);
                            const daySlots = getSlotsForDate(dateStr);
                            const holidayInfo = isHoliday(dateStr);
                            
                            return (
                            <div 
                              key={index} 
                              className={`min-h-[200px] p-2 border rounded ${
                                isCurrentMonth ? (holidayInfo ? 'bg-orange-50' : 'bg-white') : 'bg-gray-50 text-gray-400'
                              }`}
                            >
                              <div className="font-bold mb-2 text-center">
                                {date.getDate()}
                              </div>
                              
                              <div className="space-y-2">
                                {/* Show holiday if present */}
                                {holidayInfo && (
                                  <div className={`p-2 rounded text-xs border ${
                                    holidayInfo.is_staff_holiday 
                                      ? 'bg-red-100 border-red-300 text-red-800' 
                                      : 'bg-orange-100 border-orange-300 text-orange-800'
                                  }`}>
                                    <div className="font-semibold mb-1 flex items-center justify-center">
                                      {holidayInfo.is_staff_holiday ? '🏖️ Staff Holiday' : '🎉 Holiday'}
                                    </div>
                                    <div className="font-medium text-center">
                                      {holidayInfo.name}
                                    </div>
                                    <div className="text-center text-xs mt-1 capitalize opacity-75">
                                      {holidayInfo.holiday_type}
                                    </div>
                                  </div>
                                )}
                                
                                {daySlots.map((slot, idx) => (
                                  <div key={idx} className="bg-blue-50 p-2 rounded text-xs border border-blue-200">
                                    {/* Time slot */}
                                    <div className="font-semibold text-blue-900 mb-1">
                                      {slot.time_slot}
                                    </div>
                                    
                                    {/* Subject */}
                                    <div className="font-medium text-blue-800 flex items-center mb-1">
                                      <BookOpen className="w-3 h-3 mr-1" />
                                      <span className="truncate" title={slot.subject_name}>
                                        {slot.subject_name}
                                      </span>
                                    </div>
                                    
                                    {/* Teacher */}
                                    <div className="text-gray-700 flex items-center mb-1">
                                      <GraduationCap className="w-3 h-3 mr-1" />
                                      <span className="truncate" title={slot.teacher_name}>
                                        {slot.teacher_name}
                                      </span>
                                    </div>
                                    
                                    {/* Classroom */}
                                    <div className="text-gray-600 flex items-center mb-1">
                                      <Building className="w-3 h-3 mr-1" />
                                      <span className="truncate" title={slot.classroom_name}>
                                        {slot.classroom_name}
                                      </span>
                                    </div>
                                    
                                    {/* Batch name if available */}
                                    {slot.batch_name && (
                                      <div className="text-xs">
                                        <Badge variant="outline" className="text-xs">
                                          {slot.batch_name}
                                        </Badge>
                                      </div>
                                    )}
                                  </div>
                                ))}
                                
                                {/* Show if no sessions for the day */}
                                {daySlots.length === 0 && isCurrentMonth && !holidayInfo && (
                                  <div className="text-xs text-gray-400 text-center mt-4">
                                    No classes
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        });
                        })()}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
            
            {/* Lunch Break Note - Below the Monthly View Card */}
            {!loading && timetableData?.timetable_slots?.length > 0 && (
              <div className="mx-0 px-4 py-3 bg-amber-100 border-2 border-amber-300 rounded-lg mt-2 shadow-sm">
                <p className="text-sm text-amber-900 font-semibold text-center">
                  📢 Note: 1:15 PM to 1:45 PM will be Lunch Break for all staff and students
                </p>
              </div>
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* No data states */}
      {(selectedBatches.length > 0 || selectedTeachers.length > 0 || selectedClassrooms.length > 0) && 
       (!timetableData?.timetable_slots || timetableData.timetable_slots.length === 0) && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Timetable Data</h3>
            <p className="text-gray-600">
              No scheduled classes found for the selected filters and time range.
            </p>
          </CardContent>
        </Card>
      )}

      {selectedBatches.length === 0 && selectedTeachers.length === 0 && selectedClassrooms.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Grid3X3 className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select Filters</h3>
            <p className="text-gray-600">
              Choose batches, teachers, or classrooms to view their timetables.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Export Modal */}
      <Dialog open={showExportModal} onOpenChange={setShowExportModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center">
              <Calendar className="w-5 h-5 mr-2" />
              Export {exportType.toUpperCase()} - Select Date Range
            </DialogTitle>
            <DialogDescription>
              Choose the date range for your enhanced timetable export.
            </DialogDescription>
          </DialogHeader>
          
          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right font-medium">From:</label>
              <input
                type="date"
                value={exportFromDate}
                onChange={(e) => setExportFromDate(e.target.value)}
                className="col-span-3 px-3 py-2 border rounded"
              />
            </div>
            <div className="grid grid-cols-4 items-center gap-4">
              <label className="text-right font-medium">To:</label>
              <input
                type="date"
                value={exportToDate}
                onChange={(e) => setExportToDate(e.target.value)}
                className="col-span-3 px-3 py-2 border rounded"
              />
            </div>
            
            <div className="bg-blue-50 p-3 rounded">
              <div className="text-sm text-blue-800">
                <strong>Export Details:</strong><br />
                Range: {exportFromDate} to {exportToDate}<br />
                Format: {exportType.toUpperCase()}<br />
                Filters: {selectedBatches.length + selectedTeachers.length + selectedClassrooms.length} selected
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExportModal(false)}>
              Cancel
            </Button>
            <Button onClick={handleExportConfirm} disabled={!exportFromDate || !exportToDate}>
              Export {exportType.toUpperCase()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default EnhancedTimetable;